import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

const COLLECTION = 'claudeSkills';
/** CRM-managed display settings: user-created sections (order = array order). */
const SETTINGS_COLLECTION = 'claudeSkillsSettings';
const SETTINGS_CONFIG_ID = 'config';

export const DEFAULT_SKILL_SECTIONS = [{ id: 'general', label: 'General', emoji: '✨' }];

function sanitizeFileName(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function assertSkillFile(file) {
  if (!file) throw new Error('.skill file is required.');
  if (!file.name.toLowerCase().endsWith('.skill')) {
    throw new Error('File must be a .skill file.');
  }
  if (file.size > 15 * 1024 * 1024) throw new Error('File must be 15MB or smaller.');
}

// ---- Sections (created/renamed/reordered from CRM; shown on the portal) ----

export async function getClaudeSkillSections() {
  try {
    const snap = await getDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_CONFIG_ID));
    const sections = snap.exists() ? snap.data().sections : null;
    return Array.isArray(sections) && sections.length > 0 ? sections : DEFAULT_SKILL_SECTIONS;
  } catch (e) {
    console.warn('Skill sections load:', e);
    return DEFAULT_SKILL_SECTIONS;
  }
}

export async function saveClaudeSkillSections(sections) {
  await setDoc(
    doc(db, SETTINGS_COLLECTION, SETTINGS_CONFIG_ID),
    { sections, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/** Stable slug id for a new section label, deduped against existing sections. */
export function makeSkillSectionId(label, sections = []) {
  const base =
    String(label)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'section';
  const taken = new Set(sections.map((s) => s.id));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/** Skills keep working if their section was deleted: fall back to the first section. */
export function effectiveSectionId(row, sections) {
  const ids = new Set(sections.map((s) => s.id));
  return row?.sectionId && ids.has(row.sectionId) ? row.sectionId : sections[0]?.id;
}

// ---- Skills ----

export async function listClaudeSkills() {
  const snap = await getDocs(collection(db, COLLECTION));
  const rows = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    likes: d.data().likes ?? 0,
    likedBy: d.data().likedBy ?? [],
    authorName: d.data().authorName || (d.data().source === 'crm' ? 'BBA Team' : 'Community'),
  }));
  // Manual order (sortOrder, written on reorder) wins; rows without one — new
  // uploads and pre-reorder docs — come first, newest first, until placed.
  rows.sort((a, b) => {
    const ao = typeof a.sortOrder === 'number';
    const bo = typeof b.sortOrder === 'number';
    if (ao && bo) return a.sortOrder - b.sortOrder;
    if (ao !== bo) return ao ? 1 : -1;
    const ta = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
    return tb - ta;
  });
  return rows;
}

export async function uploadClaudeSkill({
  title,
  description,
  skillFile,
  sectionId = 'general',
  emoji = '',
  userId,
  authorName,
  source = 'community',
}) {
  const titleTrim = title?.trim();
  if (!titleTrim) throw new Error('Title is required.');
  assertSkillFile(skillFile);

  const storagePath = `technology-community/claude-skills/${userId}/${Date.now()}_${sanitizeFileName(skillFile.name)}`;
  const storageRef = ref(storage, storagePath);
  const snapshot = await uploadBytes(storageRef, skillFile);
  const downloadURL = await getDownloadURL(snapshot.ref);

  const docRef = await addDoc(collection(db, COLLECTION), {
    title: titleTrim,
    description: description?.trim() || '',
    sectionId,
    emoji: emoji || null,
    version: '1.0',
    authorId: userId,
    authorName: authorName?.trim() || 'Unknown',
    source,
    likes: 0,
    likedBy: [],
    skillFile: {
      fileName: skillFile.name,
      storagePath,
      downloadURL,
      size: skillFile.size,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return { id: docRef.id };
}

/** Update title/description and optionally move the skill to another section. */
export async function updateClaudeSkillMetadata({ docId, title, description, sectionId }) {
  const titleTrim = (title ?? '').trim();
  if (!titleTrim) throw new Error('Title cannot be empty.');
  const payload = {
    title: titleTrim,
    description: (description ?? '').trim(),
    updatedAt: serverTimestamp(),
  };
  if (sectionId) payload.sectionId = sectionId;
  await updateDoc(doc(db, COLLECTION, docId), payload);
}

/** Set or clear ('' / null) the emoji shown on one skill's card. */
export async function updateClaudeSkillEmoji({ docId, emoji }) {
  await updateDoc(doc(db, COLLECTION, docId), { emoji: emoji || null });
}

/** Persist display order (portal shows the same order). */
export async function saveClaudeSkillsOrder(orderedIds) {
  const batch = writeBatch(db);
  orderedIds.forEach((id, idx) => {
    batch.update(doc(db, COLLECTION, id), { sortOrder: idx });
  });
  await batch.commit();
}

export async function deleteClaudeSkill({ docId }) {
  const snap = await getDoc(doc(db, COLLECTION, docId));
  if (!snap.exists()) return;
  const data = snap.data();
  const currentPath = data.skillFile?.storagePath;

  // Old version files + entries go with the skill (best-effort; the current
  // file's path may also appear as a version entry — the second delete warns).
  try {
    const versionsSnap = await getDocs(skillVersionsRef(docId));
    await Promise.all(
      versionsSnap.docs.map(async (v) => {
        const vPath = v.data()?.storagePath;
        if (vPath && vPath !== currentPath) {
          try {
            await deleteObject(ref(storage, vPath));
          } catch (e) {
            console.warn('Version storage delete:', e);
          }
        }
        await deleteDoc(v.ref);
      })
    );
  } catch (e) {
    console.warn('Version history delete:', e);
  }

  if (currentPath) {
    try {
      await deleteObject(ref(storage, currentPath));
    } catch (e) {
      console.warn('Storage delete:', e);
    }
  }
  await deleteDoc(doc(db, COLLECTION, docId));
}

// ---- Version history (mirrors templatesImportsService's versions subcollection) ----

const skillVersionsRef = (docId) => collection(db, COLLECTION, docId, 'versions');

const toIso = (ts) => {
  try {
    const d = typeof ts?.toDate === 'function' ? ts.toDate() : ts ? new Date(ts) : new Date();
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  } catch {
    return new Date().toISOString();
  }
};

/** Lazily seed the first version entry from the parent's current file (pre-history docs). */
async function seedInitialSkillVersionIfNeeded(docId, parentData) {
  const snap = await getDocs(skillVersionsRef(docId));
  if (!snap.empty) return;
  const f = parentData?.skillFile;
  if (!f?.downloadURL) return;
  await addDoc(skillVersionsRef(docId), {
    version: parentData.version || '1.0',
    fileName: f.fileName || '',
    size: f.size || 0,
    downloadURL: f.downloadURL,
    storagePath: f.storagePath || '',
    uploadedByUid: parentData.authorId || '',
    uploadedByName: parentData.authorName || 'Unknown',
    uploadedAt: toIso(parentData.updatedAt || parentData.createdAt),
    notes: 'Imported from prior upload',
  });
}

/**
 * Upload a new .skill file version: appends an immutable version entry and
 * updates the parent's current file. Old files stay in Storage — version
 * entries reference them.
 */
export async function uploadClaudeSkillVersion({ docId, file, version, notes = '', user = {} }) {
  if (!version || !version.trim()) throw new Error('A version number is required.');
  assertSkillFile(file);

  const mainRef = doc(db, COLLECTION, docId);
  const snap = await getDoc(mainRef);
  if (!snap.exists()) throw new Error('Skill not found.');
  const data = snap.data();

  const storagePath = `technology-community/claude-skills/${user.uid || 'crm'}/${Date.now()}_${sanitizeFileName(file.name)}`;
  const snapshot = await uploadBytes(ref(storage, storagePath), file);
  const downloadURL = await getDownloadURL(snapshot.ref);

  await seedInitialSkillVersionIfNeeded(docId, data);

  await updateDoc(mainRef, {
    skillFile: {
      fileName: file.name,
      storagePath,
      downloadURL,
      size: file.size,
    },
    version: version.trim(),
    updatedAt: serverTimestamp(),
  });

  await addDoc(skillVersionsRef(docId), {
    version: version.trim(),
    fileName: file.name,
    size: file.size,
    downloadURL,
    storagePath,
    uploadedByUid: user.uid || '',
    uploadedByName: user.name || 'Unknown',
    uploadedAt: new Date().toISOString(),
    notes: notes.trim(),
  });

  return { docId, downloadURL, storagePath };
}

/** List a skill's version history, newest first. */
export async function fetchClaudeSkillVersionHistory(docId) {
  if (!docId) return [];
  const snap = await getDoc(doc(db, COLLECTION, docId));
  if (snap.exists()) {
    await seedInitialSkillVersionIfNeeded(docId, snap.data());
  }
  const q = query(skillVersionsRef(docId), orderBy('uploadedAt', 'desc'));
  const versionsSnap = await getDocs(q);
  return versionsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Point the parent back at a prior version's file and append a new version
 * entry recording the rollback (history is never destroyed).
 */
export async function rollbackClaudeSkillToVersion(docId, versionId, user = {}) {
  if (!docId || !versionId) throw new Error('docId and versionId are required');
  const versionSnap = await getDoc(doc(db, COLLECTION, docId, 'versions', versionId));
  if (!versionSnap.exists()) throw new Error('Version not found.');
  const v = versionSnap.data();

  await updateDoc(doc(db, COLLECTION, docId), {
    skillFile: {
      fileName: v.fileName,
      storagePath: v.storagePath,
      downloadURL: v.downloadURL,
      size: v.size || 0,
    },
    version: v.version,
    updatedAt: serverTimestamp(),
  });

  const versionEntry = await addDoc(skillVersionsRef(docId), {
    version: v.version,
    fileName: v.fileName,
    size: v.size || 0,
    downloadURL: v.downloadURL,
    storagePath: v.storagePath,
    uploadedByUid: user.uid || '',
    uploadedByName: user.name || 'Unknown',
    uploadedAt: new Date().toISOString(),
    notes: `Rolled back to v${v.version}`,
  });

  return { versionId: versionEntry.id };
}

export { COLLECTION as CLAUDE_SKILLS_COLLECTION };
